import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { FileBarChart, FileSpreadsheet, FileText, Presentation } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useIntl } from 'react-intl';

/**
 * The artefact kinds this card draws an icon for, in AGENTS vocabulary.
 *
 * The card used to type this prop as `ArtefactType` from
 * `@heidi/evidence/contracts`, and that one type-only import is what held the
 * module app-side: the `/ui` zone permits a foreign domain's inert contracts
 * door, but every external a package names has to be a declared dependency, and
 * declaring `@heidi/evidence` here is an `agents -> evidence` manifest edge plus
 * an Nx scope grant.
 *
 * Under the wire-adapter standard the package declares the prop type it renders
 * against and the APP maps the wire type onto it — one explicit function at
 * `src/features/evidence/adapters/to-export-artefact-kind.ts`, which is the only
 * place the two sets meet and therefore the only place their drift can surface.
 * This union is not, and must never be documented as, assignable-from
 * `ArtefactType`: what makes the join safe is the adapter's own compilation, not
 * a structural coincidence between two lists.
 *
 * The members are the ones `ICON_BY_KIND` needs a row for. They coincide with
 * `ArtefactType`'s seven exactly today, measured while writing the adapter; a
 * member added or removed on either side is a `tsc` error inside it.
 */
export type ExportArtefactKind =
  | 'csv'
  | 'excel'
  | 'word'
  | 'pptx'
  | 'pdf'
  | 'patient_handout'
  | 'form_fill';

const ICON_BY_KIND: Record<ExportArtefactKind, typeof FileText> = {
  csv: FileSpreadsheet,
  excel: FileSpreadsheet,
  word: FileText,
  pdf: FileText,
  patient_handout: FileText,
  pptx: Presentation,
  form_fill: FileBarChart,
};

export interface ExportGeneratingCardProps {
  artefactType: ExportArtefactKind;
  title: string;
}

/**
 * Animated "preparing an artefact" card shown while a report/export tool runs
 * (PDF, Word, Excel, CSV, PowerPoint). Replaces the previously blank generating
 * state so the timeline shows tangible progress. Honours reduced-motion.
 */
export const ExportGeneratingCard = ({
  artefactType,
  title,
}: ExportGeneratingCardProps) => {
  const intl = useIntl();
  const reduceMotion = useReducedMotion();
  const Icon = ICON_BY_KIND[artefactType] ?? FileText;

  return (
    <div
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4"
      data-testid="export-generating-card"
      data-artefact-type={artefactType}
    >
      <m.span
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-page text-text-secondary"
        animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Icon className="size-4.5" />
      </m.span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <TypographyP2Bold className="truncate text-text-primary">
          {title}
        </TypographyP2Bold>
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-surface-page">
          <m.span
            className={cn(
              'absolute inset-y-0 left-0 w-1/3 rounded-full',
              'bg-gradient-to-r from-transparent via-text-tertiary/60 to-transparent'
            )}
            animate={reduceMotion ? undefined : { x: ['-100%', '400%'] }}
            transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
        <TypographyCaption className="text-text-tertiary">
          {intl.formatMessage({
            id: 'Ob8gKId5MI',
            defaultMessage: 'Preparing…',
          })}
        </TypographyCaption>
      </div>
    </div>
  );
};
